<template>
  <div class="subscription-page">

    <header class="sub-header">
      <button class="back-btn" @click="$router.back()">
        <i class="ph ph-arrow-left"></i>
      </button>
      <h1>구독 플랜</h1>
    </header>

    <!-- 현재 구독 상태 -->
    <div class="current-plan" v-if="currentSub">
      <div class="plan-badge active">현재 플랜</div>
      <h3>{{ currentSub.plan_name }}</h3>
      <p>{{ formatDate(currentSub.next_billing_date) }} 갱신 예정</p>
      <button class="cancel-btn" @click="showCancelConfirm = true">구독 해지</button>
    </div>

    <!-- 플랜 목록 -->
    <div class="plans-grid">
      <div
        v-for="plan in plans"
        :key="plan.id"
        class="plan-card"
        :class="{ recommended: plan.recommended, current: currentSub?.plan_id === plan.id }"
        @click="selectPlan(plan)"
      >
        <div class="plan-tag" v-if="plan.recommended">추천</div>
        <div class="plan-tag current-tag" v-if="currentSub?.plan_id === plan.id">사용중</div>
        <h2>{{ plan.name }}</h2>
        <div class="plan-price">
          <span class="price">{{ formatPrice(plan.price) }}</span>
          <span class="period">/{{ plan.interval === 'month' ? '월' : '년' }}</span>
        </div>
        <ul class="plan-features">
          <li v-for="(feat, i) in plan.features" :key="i">
            <i class="ph ph-check-circle"></i>
            {{ feat }}
          </li>
        </ul>
        <button
          class="subscribe-btn"
          :class="{ active: currentSub?.plan_id === plan.id }"
          :disabled="currentSub?.plan_id === plan.id"
        >
          {{ currentSub?.plan_id === plan.id ? '현재 플랜' : '구독하기' }}
        </button>
      </div>
    </div>

    <!-- 결제 내역 -->
    <div class="payment-history" v-if="paymentHistory.length">
      <h2>결제 내역</h2>
      <div class="history-list">
        <div class="history-item" v-for="item in paymentHistory" :key="item.idx">
          <div class="history-info">
            <span class="history-name">{{ item.plan_name }}</span>
            <span class="history-date">{{ formatDate(item.paid_at) }}</span>
          </div>
          <div class="history-amount" :class="item.status">
            {{ item.status === 'cancelled' ? '환불' : '' }}
            {{ formatPrice(item.amount) }}원
          </div>
        </div>
      </div>
    </div>

    <!-- 해지 확인 모달 -->
    <div class="modal-overlay" v-if="showCancelConfirm" @click.self="showCancelConfirm = false">
      <div class="modal-box">
        <h3>구독을 해지하시겠습니까?</h3>
        <p>현재 결제 기간이 끝나면 서비스가 중단됩니다.<br>남은 기간 동안은 계속 이용 가능합니다.</p>
        <div class="modal-actions">
          <button class="modal-cancel" @click="showCancelConfirm = false">취소</button>
          <button class="modal-confirm" @click="cancelSubscription">해지하기</button>
        </div>
      </div>
    </div>

    <!-- 로딩 -->
    <div class="loading-overlay" v-if="isProcessing">
      <div class="spinner"></div>
      <p>결제 처리 중...</p>
    </div>

  </div>
</template>


<script>
import { ref, inject, onMounted } from 'vue'

export default {
  name: 'SubscriptionPage',
  setup() {
    const sql_query = inject('sql_query')
    const getMbid = inject('getMbid')
    const member_info = inject('member_info')
    const requestBillingKey = inject('requestBillingKey')
    const cancelBillingSubscription = inject('cancelBillingSubscription')
    const getSubscriptionInfo = inject('getSubscriptionInfo')
    const getPaymentHistory = inject('getPaymentHistory')

    // 기본 플랜 (DB 없을 때 fallback)
    const DEFAULT_PLANS = [
      {
        id: 1, name: 'Free', price: 0, interval: 'month', recommended: false,
        features: ['고객 50명 관리', '보장분석 기본', '커뮤니티 접근']
      },
      {
        id: 2, name: 'Pro', price: 9900, interval: 'month', recommended: true,
        features: ['고객 무제한', '보장분석 고급', 'SMS 발송 100건/월', '전자명함', '선물하기']
      },
      {
        id: 3, name: 'Premium', price: 89000, interval: 'year', recommended: false,
        features: ['Pro 전체 기능', 'SMS 무제한', '우선 고객지원', '팀 관리 기능', 'API 연동']
      }
    ]

    const plans = ref([])
    const currentSub = ref(null)
    const paymentHistory = ref([])
    const showCancelConfirm = ref(false)
    const isProcessing = ref(false)

    // 플랜 목록 로드 (DB 실패 시 기본 플랜 사용)
    async function loadPlans() {
      try {
        const rows = await sql_query(
          `SELECT * FROM subscription_plans WHERE is_active = 1 ORDER BY sort_order ASC`
        )
        if (Array.isArray(rows) && rows.length > 0) {
          plans.value = rows.map(r => ({
            id: r.id,
            name: r.name,
            price: r.price,
            interval: r.billing_interval,
            features: r.features ? JSON.parse(r.features) : [],
            recommended: r.is_recommended === 1
          }))
          return
        }
      } catch (e) {
        console.warn('플랜 DB 로드 실패, 기본 플랜 사용:', e)
      }
      plans.value = DEFAULT_PLANS
    }

    // 현재 구독 정보 로드
    async function loadCurrentSubscription() {
      const mbId = getMbid()
      if (!mbId) return
      const info = await getSubscriptionInfo(mbId)
      currentSub.value = info
    }

    // 결제 내역 로드
    async function loadPaymentHistory() {
      const mbId = getMbid()
      if (!mbId) return
      const history = await getPaymentHistory(mbId)
      paymentHistory.value = history || []
    }

    // 플랜 선택 → 빌링키 발급 → 구독 생성
    async function selectPlan(plan) {
      if (currentSub.value?.plan_id === plan.id) return

      const mbId = getMbid()
      if (!mbId) {
        alert('로그인이 필요합니다.')
        return
      }

      isProcessing.value = true
      try {
        const result = await requestBillingKey({
          planId: plan.id,
          planName: plan.name,
          price: plan.price,
          interval: plan.interval,
          customerName: member_info.name,
          customerEmail: member_info.email,
          customerPhone: member_info.phone
        })

        if (result.success) {
          alert('구독이 시작되었습니다!')
          await loadCurrentSubscription()
          await loadPaymentHistory()
        } else {
          alert(result.message || '결제에 실패했습니다.')
        }
      } catch (e) {
        console.error('구독 처리 실패:', e)
        alert('결제 처리 중 오류가 발생했습니다.')
      } finally {
        isProcessing.value = false
      }
    }

    // 구독 해지
    async function cancelSubscription() {
      showCancelConfirm.value = false
      isProcessing.value = true

      try {
        const result = await cancelBillingSubscription(getMbid())
        if (result.success) {
          alert('구독이 해지되었습니다. 남은 기간까지 이용 가능합니다.')
          await loadCurrentSubscription()
        } else {
          alert(result.message || '해지 처리에 실패했습니다.')
        }
      } catch (e) {
        console.error('해지 실패:', e)
        alert('해지 처리 중 오류가 발생했습니다.')
      } finally {
        isProcessing.value = false
      }
    }

    function formatPrice(n) {
      return Number(n).toLocaleString('ko-KR')
    }
    function formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('ko-KR')
    }

    onMounted(() => {
      loadPlans()
      loadCurrentSubscription()
      loadPaymentHistory()
    })

    return {
      plans, currentSub, paymentHistory,
      showCancelConfirm, isProcessing,
      selectPlan, cancelSubscription,
      formatPrice, formatDate
    }
  }
}
</script>


<style scoped>
.subscription-page {
  width: 100%;
  min-height: 100vh;
  background: var(--color-background);
  padding: 0 0 80px;
  box-sizing: border-box;
}

.sub-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  position: sticky;
  top: 0;
  background: var(--color-background);
  z-index: 10;
  border-bottom: 1px solid var(--color-border, rgba(128,128,128,0.15));
}
.sub-header h1 {
  margin: 0;
  font-size: 20px;
  color: var(--color-text-primary);
}
.back-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--color-text-primary);
  padding: 4px;
}

/* 현재 구독 */
.current-plan {
  margin: 20px;
  padding: 20px;
  border-radius: 16px;
  background: var(--color-surface-deep);
  border: 1px solid var(--color-border, rgba(128,128,128,0.15));
}
.current-plan h3 {
  margin: 8px 0 4px;
  font-size: 18px;
  color: var(--color-text-primary);
}
.current-plan p {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--color-text-secondary);
}
.plan-badge.active {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}
.cancel-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.cancel-btn:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* 플랜 그리드 */
.plans-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 20px;
  margin-top: 20px;
}

.plan-card {
  position: relative;
  padding: 24px;
  border-radius: 16px;
  background: var(--color-surface-deep);
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.25s ease;
}
.plan-card:hover {
  border-color: var(--color-text-secondary);
  transform: translateY(-2px);
}
.plan-card.recommended {
  border-color: #f59e0b;
}
.plan-card.current {
  border-color: #22c55e;
  opacity: 0.7;
}

.plan-tag {
  position: absolute;
  top: -10px;
  right: 16px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  background: #f59e0b;
  color: #fff;
}
.plan-tag.current-tag {
  background: #22c55e;
}

.plan-card h2 {
  margin: 0 0 8px;
  font-size: 20px;
  color: var(--color-text-primary);
}

.plan-price {
  margin-bottom: 16px;
}
.plan-price .price {
  font-size: 28px;
  font-weight: 800;
  color: var(--color-text-primary);
}
.plan-price .period {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-left: 2px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plan-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--color-text-primary);
}
.plan-features li i {
  color: #22c55e;
  font-size: 18px;
}

.subscribe-btn {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--color-background-reverse);
  color: var(--color-background);
}
.subscribe-btn:hover:not(:disabled) {
  opacity: 0.85;
  transform: scale(1.01);
}
.subscribe-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.subscribe-btn.active {
  background: #22c55e;
  color: #fff;
}

/* 결제 내역 */
.payment-history {
  margin: 32px 20px 0;
}
.payment-history h2 {
  font-size: 18px;
  color: var(--color-text-primary);
  margin: 0 0 12px;
}
.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--color-surface-deep);
}
.history-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.history-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}
.history-date {
  font-size: 12px;
  color: var(--color-text-secondary);
}
.history-amount {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
}
.history-amount.cancelled {
  color: #ef4444;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.modal-box {
  background: var(--color-background);
  border-radius: 20px;
  padding: 28px 24px;
  max-width: 360px;
  width: 100%;
  text-align: center;
}
.modal-box h3 {
  margin: 0 0 12px;
  font-size: 18px;
  color: var(--color-text-primary);
}
.modal-box p {
  margin: 0 0 24px;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}
.modal-actions {
  display: flex;
  gap: 10px;
}
.modal-cancel, .modal-confirm {
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}
.modal-cancel {
  background: var(--color-surface-deep);
  color: var(--color-text-primary);
}
.modal-confirm {
  background: #ef4444;
  color: #fff;
}

/* 로딩 */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  gap: 16px;
}
.loading-overlay p {
  color: #fff;
  font-size: 16px;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255,255,255,0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 600px) {
  .plans-grid {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 800px;
    margin: 20px auto 0;
  }
  .plan-card {
    flex: 1;
    min-width: 240px;
    max-width: 360px;
  }
}
</style>
